import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataPanelItem, DebugView, FaIcon, FloatingPanel, NgButton, PanelErrorWithIcon, PanelInfoWithIcon } from '@/ui';
import type { FloatingPanelState } from '@/ui';
import { LiveVideoSource } from '@/signalr';
import type { LiveVideo } from '@/signalr';
import { useIncidentRealtime } from '@/features/incident-detail/hub/incidentRealtime';
import { requestEedVideo } from '@/features/incident-detail/api/incidentDetailApi';
import { useVideoCall } from './useVideoCall';
import { MediasoupVideoViewer } from './MediasoupVideoViewer';
import { VideoBoxLayout } from './VideoBoxLayout';
import {
  CALLER_VIDEO_CONNECTION_STATE_NAMES,
  canSwapCameraOrTakeSnapshot,
  getDisplayPanelMode,
  getWebAppStateDescriptionKey,
  getWebAppStateIcon,
  getWebAppStateTextKey,
  isRewindDisabled,
} from './videoCallMachine';
import type { DisplayPanelMode, VideoCallState } from './videoCallMachine';

export type MediasoupVideoBoxProps = {
  incidentId: string;
  liveVideo: LiveVideo;
  videoExpanded: boolean;
  onVideoExpandedChanged: (expanded: boolean) => void;
  eedVideoCallId?: string | null;
};

const FLOATING_STATE_CSS: Record<FloatingPanelState, string> = {
  docked: 'mediasoup-video-box--floating-state-docked',
  floating: 'mediasoup-video-box--floating-state-floating',
  fullscreen: 'mediasoup-video-box--floating-state-fullscreen',
};

export function MediasoupVideoBox({
  incidentId,
  liveVideo,
  videoExpanded,
  onVideoExpandedChanged,
  eedVideoCallId,
}: MediasoupVideoBoxProps) {
  void incidentId;
  const { t } = useTranslation();
  const realtime = useIncidentRealtime();

  const resendEedVideoRequest = async () => {
    if (eedVideoCallId == null) {
      return;
    }
    await requestEedVideo(eedVideoCallId, liveVideo.id);
  };

  const { state, videoServerUrl, toggleRecordingAndPauseOrResumeStream, switchUserCamera, takeSnapshot, endLiveVideo } =
    useVideoCall({ liveVideo, realtime, onVideoExpandedChanged });

  const [floatingPanelState, setFloatingPanelState] = useState<FloatingPanelState>('docked');

  const isEedVideo = liveVideo.source === LiveVideoSource.Eed;
  const eedCssClass = isEedVideo ? 'mediasoup-video-box--eed' : '';
  const videoExpandedCssClass = videoExpanded ? 'mediasoup-video-box--expanded' : '';
  const floatingStateCssClass = FLOATING_STATE_CSS[floatingPanelState];

  const { showRecording } = state;
  const hideControls = showRecording;
  const liveIndicatorCssClass = showRecording
    ? 'mediasoup-video-box__live--inactive'
    : 'mediasoup-video-box__live--active';
  const liveStreamLabel =
    liveVideo.source === LiveVideoSource.Eed
      ? t('Incident_Video_EedLiveStream')
      : liveVideo.source === LiveVideoSource.Els
        ? t('Incident_Video_ElsLiveStream')
        : t('Incident_Video_WebAppLiveStream');

  const currentDisplayMode = getDisplayPanelMode(state, isEedVideo);

  const serverStatsUrl = `${videoServerUrl ?? ''}/${liveVideo.id}/stats/server`;
  const consumerStatsUrl = `${videoServerUrl ?? ''}/${liveVideo.id}/stats/consumer`;
  const producerStatsUrl = `${videoServerUrl ?? ''}/${liveVideo.id}/stats/producer`;
  const logsStatsUrl = `${videoServerUrl ?? ''}/${liveVideo.id}/stats/logs`;

  const onVideoBoxClicked = () => {
    onVideoExpandedChanged(!videoExpanded);
  };

  const webAppStateText = t(getWebAppStateTextKey(state.webAppState));
  const webAppStateDescriptionKey = getWebAppStateDescriptionKey(state.webAppState);
  const webAppStateDescription = webAppStateDescriptionKey !== null ? t(webAppStateDescriptionKey) : '';

  return (
    <FloatingPanel
      state={floatingPanelState}
      onStateChanged={setFloatingPanelState}
      floatingDisabledOnMobile
      disableFloating
      disableFullscreen
      floatingDisabled
    >
      <DebugView>
        <DataPanelItem title="VideoId" value={liveVideo.id} />
        <DataPanelItem
          title="CallerVideoConnectionState"
          value={CALLER_VIDEO_CONNECTION_STATE_NAMES[state.callerConnectionState]}
        />
        <DataPanelItem title="CurrentDisplayModeByPriority" value={currentDisplayMode} />
        {videoServerUrl != null && videoServerUrl !== '' && (
          <DataPanelItem title="Stats">
            <span>
              <a href={serverStatsUrl} target="_blank">
                Server
              </a>
              <a href={consumerStatsUrl} target="_blank">
                Consumer
              </a>
              <a href={producerStatsUrl} target="_blank">
                Producer
              </a>
              <a href={logsStatsUrl} target="_blank">
                Logs
              </a>
            </span>
          </DataPanelItem>
        )}
      </DebugView>

      <div
        className={`mediasoup-video-box ${floatingStateCssClass} ${eedCssClass} ${videoExpandedCssClass}`}
        onClick={onVideoBoxClicked}
      >
        <button
          className="mediasoup-video-box__expand-btn"
          onClick={(event) => {
            event.stopPropagation();
            onVideoBoxClicked();
          }}
          title={t('Incident_Video_ExpandVideo')}
        >
          <FaIcon icon={videoExpanded ? 'fal fa-compress' : 'fal fa-expand'} />
        </button>

        <VideoBoxLayoutContent
          state={state}
          isEedVideo={isEedVideo}
          currentDisplayMode={currentDisplayMode}
          hideControls={hideControls}
          liveIndicatorCssClass={liveIndicatorCssClass}
          liveStreamLabel={liveStreamLabel}
          webAppStateText={webAppStateText}
          webAppStateDescription={webAppStateDescription}
          eedVideoCallId={eedVideoCallId ?? null}
          onResendEedVideoRequest={resendEedVideoRequest}
          toggleRecordingAndPauseOrResumeStream={toggleRecordingAndPauseOrResumeStream}
          switchUserCamera={switchUserCamera}
          takeSnapshot={takeSnapshot}
          endLiveVideo={endLiveVideo}
        />
      </div>
    </FloatingPanel>
  );
}

type VideoBoxLayoutContentProps = {
  state: VideoCallState;
  isEedVideo: boolean;
  currentDisplayMode: DisplayPanelMode;
  hideControls: boolean;
  liveIndicatorCssClass: string;
  liveStreamLabel: string;
  webAppStateText: string;
  webAppStateDescription: string;
  eedVideoCallId: string | null;
  onResendEedVideoRequest: () => Promise<void>;
  toggleRecordingAndPauseOrResumeStream: () => Promise<void>;
  switchUserCamera: () => Promise<void>;
  takeSnapshot: () => Promise<void>;
  endLiveVideo: () => Promise<void>;
};

function VideoBoxLayoutContent({
  state,
  isEedVideo,
  currentDisplayMode,
  hideControls,
  liveIndicatorCssClass,
  liveStreamLabel,
  webAppStateText,
  webAppStateDescription,
  eedVideoCallId,
  onResendEedVideoRequest,
  toggleRecordingAndPauseOrResumeStream,
  switchUserCamera,
  takeSnapshot,
  endLiveVideo,
}: VideoBoxLayoutContentProps) {
  const { t } = useTranslation();
  const { showRecording } = state;

  return (
    <VideoBoxLayout
      contentTemplate={
        <>
          {state.callerConnectionState === 'Connected' && (
            <p className={liveIndicatorCssClass}>
              {showRecording ? t('Incident_Video_RecordingPlaying') : liveStreamLabel}
            </p>
          )}

          <div
            className={`mediasoup-video-box__viewer-wrapper ${
              currentDisplayMode === 'LiveVideo' || currentDisplayMode === 'RecordingPlayback' ? '' : 'd-none'
            }`}
          >
            <MediasoupVideoViewer
              showRecording={showRecording}
              connectionState={state.callerConnectionState}
              cameraState={state.cameraState}
            />
          </div>

          {currentDisplayMode === 'FatalError' && (
            <div className="pb-4 text-center">
              <div className="mx-3">
                <PanelErrorWithIcon title={webAppStateText} description={webAppStateDescription} centerContent />
              </div>
            </div>
          )}

          {currentDisplayMode === 'EedInstructions' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <div>{t('Incident_Video_EedScreenShareRequestSent')}</div>
                {eedVideoCallId != null && (
                  <span onClick={(event) => event.stopPropagation()}>
                    <NgButton
                      text={t('Incident_Button_ResendVideoRequest')}
                      icon="fal fa-video-plus"
                      size="small"
                      showProgress
                      onClick={onResendEedVideoRequest}
                    />
                  </span>
                )}
              </PanelInfoWithIcon>
            </div>
          )}

          {currentDisplayMode === 'Disconnected' && (
            <PanelInfoWithIcon>
              <img src="Images/Incident/video-canceled.svg" alt="Video disconnected" />
              <div>{t('Incident_Video_VideoStreamCancelled')}</div>
              <div>{t('Incident_Video_WaitingForCameraAccessNote')}</div>
            </PanelInfoWithIcon>
          )}

          {currentDisplayMode === 'Connecting' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src={getWebAppStateIcon()} alt="Connecting" />
                <div>{t('Incident_Video_ConnectingToCall')}</div>
              </PanelInfoWithIcon>
            </div>
          )}

          {currentDisplayMode === 'WebAppState' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src={getWebAppStateIcon()} alt="Video status" />
                <div>{webAppStateText}</div>
                <div className="small text-black-50">{webAppStateDescription}</div>
              </PanelInfoWithIcon>
            </div>
          )}

          {currentDisplayMode === 'CameraTurnedOff' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src="Images/Incident/video-canceled.svg" alt="Camera disabled" />
                <div>{t('Incident_Video_CameraDisabled')}</div>
                <div>{t('Incident_Video_CameraDisabledByUser')}</div>
              </PanelInfoWithIcon>
            </div>
          )}
        </>
      }
      actionsTemplate={
        <div onClick={(event) => event.stopPropagation()}>
          {!hideControls ? (
            <>
              <div className="btn-group">
                <NgButton
                  onClick={toggleRecordingAndPauseOrResumeStream}
                  type="dark"
                  disabled={isRewindDisabled(state)}
                >
                  -5s
                </NgButton>
                {!isEedVideo && (
                  <>
                    <NgButton
                      icon="fal fa-camera-rotate"
                      title={t('Button_SwapCamera')}
                      disabled={!canSwapCameraOrTakeSnapshot(state)}
                      outline
                      onClick={switchUserCamera}
                    />

                    <NgButton
                      icon="fal fa-camera"
                      title={t('Button_TakeSnapshot')}
                      disabled={!canSwapCameraOrTakeSnapshot(state)}
                      outline
                      onClick={takeSnapshot}
                    />
                  </>
                )}
              </div>

              {!isEedVideo && (
                <span onClick={(event) => event.stopPropagation()}>
                  <NgButton
                    icon="fal fa-video-slash"
                    title={t('Incident_Button_StopVideoStream')}
                    type="danger"
                    onClick={endLiveVideo}
                  />
                </span>
              )}
            </>
          ) : (
            <div className="mediasoup-video-box__go-live">
              <NgButton onClick={toggleRecordingAndPauseOrResumeStream} size="small" type="dark">
                <span>{t('Incident_Button_PlayLiveStream')}</span>
              </NgButton>
            </div>
          )}
        </div>
      }
    />
  );
}
